/**
 * Live-canvas budget.
 *
 * WebGL contexts are a scarce, device-level resource: a browser will drop the
 * oldest context once a page holds too many, which shows up as
 * "THREE.WebGLRenderer: Context Lost" and a scene that never paints. Our Businesses
 * carries five scenes, so mounting every one that happens to be near the viewport
 * is not safe.
 *
 * This module keeps a global budget of live canvases. Every SceneFrame registers
 * itself, reports whether it is near the viewport, and is granted a slot only if it
 * is among the nearest to the centre of the viewport. Everything else stays on its
 * poster, which is the same contract as the reduced-motion path.
 */
const MAX_LIVE = 2;

/** node -> { near: boolean, apply: (mounted: boolean) => void, mounted: boolean } */
const registry = new Map();
let frame = null;
let listening = false;

function distanceFromViewportCentre(node) {
  const rect = node.getBoundingClientRect();
  const centre = rect.top + rect.height / 2;
  return Math.abs(centre - window.innerHeight / 2);
}

function evaluate() {
  frame = null;
  const near = [];
  registry.forEach((state, node) => {
    if (state.near) near.push(node);
    else if (state.mounted) {
      state.mounted = false;
      state.apply(false);
    }
  });
  near.sort((a, b) => distanceFromViewportCentre(a) - distanceFromViewportCentre(b));
  near.forEach((node, index) => {
    const state = registry.get(node);
    const wanted = index < MAX_LIVE;
    if (state.mounted !== wanted) {
      state.mounted = wanted;
      state.apply(wanted);
    }
  });
}

export function scheduleEvaluate() {
  if (frame !== null || typeof window === 'undefined') return;
  frame = window.requestAnimationFrame(evaluate);
}

function listen() {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('scroll', scheduleEvaluate, { passive: true });
  window.addEventListener('resize', scheduleEvaluate, { passive: true });
}

function stopListening() {
  if (!listening || registry.size > 0 || typeof window === 'undefined') return;
  listening = false;
  window.removeEventListener('scroll', scheduleEvaluate);
  window.removeEventListener('resize', scheduleEvaluate);
}

export function register(node, apply) {
  if (!node) return () => {};
  registry.set(node, { near: false, apply, mounted: false });
  listen();
  return () => {
    registry.delete(node);
    stopListening();
    scheduleEvaluate();
  };
}

export function setNear(node, near) {
  const state = registry.get(node);
  if (!state || state.near === near) return;
  state.near = near;
  scheduleEvaluate();
}

export const LIVE_CANVAS_BUDGET = MAX_LIVE;
